import { FilePdfOutlined, PrinterOutlined } from "@ant-design/icons";
import { Button, Col, DatePicker, Empty, Form, Radio, Row, Select, Spin, Image, Flex, Skeleton } from "antd";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../util/hooks";
import { gerarRelatorioMovimentacaoBancaria } from "../../../services/relatorioService";
import { MovimentacaoBancariaModel } from "../../../models/relatorio/MovimentacaoBancariaModel";
import { buscarEmpresa } from "../../../services/empresaService";
import { buscarContaBanco } from "../../../services/contaBancoService";
import { Vm_ContaBancoModel } from "../../../models/contaBanco/vm_contaBancoModel";
import dayjs from "dayjs";
import { setTitle } from "../../../redux/slices/configuracoes/configuracoesSlice";
import { currencyBRL } from "../../../util/dinheiroBRAuxiliar";
import React from "react";
import { RelatorioMovimentacaoBancariaVM } from "../../../models/relatorio/RelatorioMovimentacaoBancariaVM";
import "./style/index.scss";
import { useReactToPrint } from "react-to-print";
import { EmpresaModel } from "../../../models/empresa/empresaModel";

interface totalAgrupamento {
  idContaBancaria: number;
  totalEntradas: number;
  totalSaidas: number;
  diferenca: number;
}

const MovimentacaoFinanceiraRelatorio = () => {
  //estados globais
  const relatorioState = useAppSelector((state) => state.relatorio);
  const empresaState = useAppSelector((state) => state.empresa);
  const contaBancoState = useAppSelector((state) => state.contaBanco);
  const persistedState = useAppSelector((state) => state.persisted);

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const [form] = Form.useForm<any>();
  const [selectedApelido, setSelectedApelido] = useState<string | null>(null);
  const [selectedConta, setSelectedConta] = useState<string | null>(null);
  const [selectedPeriodo, setSelectedPeriodo] = useState("");
  // const [listaAgrupada, setListaAgrupada] = useState<RelatorioMovimentacaoBancariaVM[]>([]);

  useEffect(() => {
    dispatch(setTitle("RELATÓRIO DE MOVIMENTAÇÃO FINANCEIRA"));
    if (persistedState.user.empresa !== null) {
      onChangeEmpresa(persistedState.user.empresa.idEmpresa);
    }
    // setListaAgrupada([]);
    dispatch(buscarEmpresa(empresaState.busca));
    form.setFieldsValue({ tipos: "T", visualizacao: true, dataInicio: dayjs().startOf("week"), dataFim: dayjs().endOf("week") });
  }, []);

  const CarregarRelatorio = (values: any) => {
    let tipoRelatoro = null;
    if (values.tipos === "E") {
      tipoRelatoro = 1;
    } else if (values.tipos === "S") {
      tipoRelatoro = -1;
    }
    let parametros: MovimentacaoBancariaModel = {
      dataInicio: dayjs(values.dataInicio).format("YYYY-MM-DD"),
      dataFim: dayjs(values.dataFim).format("YYYY-MM-DD"),
      entradaSaida: tipoRelatoro,
      detalhadaResumida: values.visualizacao,
      idContaBanco: values.idContaBanco,
    };

    //filtros
    if (persistedState.user.empresa?.idEmpresa !== null && persistedState.user.perfil !== "AUDITORIA") {
      setSelectedApelido(empresaState.list[0].apelido);
    } else {
      let empresa = empresaState.list.filter((empresa: EmpresaModel) => empresa.id === values.idEmpresa)[0].apelido;
      setSelectedApelido(empresa);
    }
    let conta = contaBancoState.list.filter((conta: { id: number }) => conta.id === values.idContaBanco)[0].denominacaoPersonalizada;
    let periodo = `${dayjs(values.dataInicio).format("DD/MM/YYYY")} à ${dayjs(values.dataFim).format("DD/MM/YYYY")}`;
    setSelectedConta(conta);
    setSelectedPeriodo(periodo);

    dispatch(gerarRelatorioMovimentacaoBancaria(parametros));
  };

  const dataAgora = dayjs().format("DD/MM/YYYY [às] HH:mm");

  const onChangeEmpresa = (value: number) => {
    form.resetFields(["idContaBanco"]);
    dispatch(buscarContaBanco({ pesquisar: null, id: null, idEmpresa: value }));
  };

  const relatorioMovimentacao = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    content: () => relatorioMovimentacao.current,
  });

  function calcularTotaisGrupo(idConta: number): totalAgrupamento[] {
    const totais: totalAgrupamento[] = relatorioState.listMovimentacaoBancaria
      .filter((c) => c.idContaBanco === idConta)
      .map((contaBancaria) => {
        let entradas = contaBancaria.movimentacoes
          .filter((t) => t.entradaSaida === "E")
          .map((t) => t.total)
          .reduce((somatorio, total) => somatorio + total, 0);

        let saidas = contaBancaria.movimentacoes
          .filter((t) => t.entradaSaida === "S")
          .map((t) => t.total)
          .reduce((somatorio, total) => somatorio + total, 0);

        return {
          idContaBancaria: contaBancaria.id,
          totalEntradas: entradas,
          totalSaidas: saidas,
          diferenca: entradas - saidas,
        };
      });

    return totais;
  }

  function groupBy<T>(arr: T[], key: keyof T) {
    return arr.reduce((acc: Record<string, T[]>, obj: T) => {
      const groupKey = obj[key] as string;
      acc[groupKey] = acc[groupKey] || [];
      acc[groupKey].push(obj);
      return acc;
    }, {});
  }

  const RelatorioHtml = React.forwardRef<HTMLDivElement>((_props, ref) => {
    return (
      <>
        <div ref={ref} className="relatorios-html-format">
          <div className="rel-cabecalho">
            <div className="rel-titulo">
              <h2>Movimentação Bancária</h2>
              <p>Gerado em {dataAgora}</p>
            </div>
            <hr />
            <p>
              Empresa: {selectedApelido}, Conta: {selectedConta}, Período: {selectedPeriodo}
            </p>
          </div>
          <div className="rel-detalhes">
            <table className="tb-dados">
              {relatorioState.listMovimentacaoBancaria.map((contaBancaria) => (
                <React.Fragment key={contaBancaria.idContaBanco}>
                  <thead style={{ fontSize: "11px" }}>
                    <tr>
                      <th colSpan={5} className="ht-left">
                        Conta bancária: {contaBancaria.nomeConta}
                      </th>
                      <th colSpan={2} className="ht-right">
                        Saldo anterior: {currencyBRL(contaBancaria.saldoAnterior)}
                      </th>
                    </tr>
                  </thead>
                  <thead>
                    <tr className="header-table">
                      <th className="ht-left">Data</th>
                      <th className="ht-left">Tipo</th>
                      <th className="ht-center">E/S</th>
                      <th className="ht-left">Cliente/Fornecedor</th>
                      <th className="ht-left">HIstórico</th>
                      <th className="ht-right">Total</th>
                      <th className="ht-right">Saldo</th>
                    </tr>
                  </thead>
                  <tbody className="body-table">
                    {contaBancaria.movimentacoes.map((t) => (
                      <React.Fragment key={t.id}>
                        <tr>
                          <td className="ht-left">{dayjs(t.data).format("DD/MM/YYYY")}</td>
                          <td className="ht-left" style={{ whiteSpace: "nowrap" }}>
                            {t.denominacao}
                          </td>
                          <td className="ht-center">{t.entradaSaida}</td>
                          <td className="ht-left">{t.clienteFornecedor}</td>
                          <td className="ht-left">{t.historico}</td>
                          <td className="ht-right">{currencyBRL(t.total)}</td>
                          <td className="ht-right">{currencyBRL(t.saldo)}</td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot className="footer-table">
                    <tr>
                      <td className="ht-left" colSpan={2}>
                        Total geral: Movimentações: {contaBancaria.movimentacoes.length}
                      </td>
                      <td className="ht-left txt-positivo">Entradas: {currencyBRL(calcularTotaisGrupo(contaBancaria.idContaBanco).map((c) => c.totalEntradas))}</td>
                      <td className="ht-left txt-negativo">Saídas: {currencyBRL(calcularTotaisGrupo(contaBancaria.idContaBanco).map((c) => c.totalSaidas))}</td>
                      <td></td>
                      <td className="ht-right">{currencyBRL(calcularTotaisGrupo(contaBancaria.idContaBanco).map((c) => c.diferenca))}</td>
                      <td className="ht-right">{currencyBRL(contaBancaria.movimentacoes.map((t) => t.saldo).pop())}</td>
                    </tr>
                  </tfoot>
                </React.Fragment>
              ))}
            </table>
          </div>
        </div>
      </>
    );
  });

  return (
    <Flex vertical>
      <Form form={form} layout="vertical" onFinish={CarregarRelatorio}>
        <Row gutter={12} align="bottom">
          <Col span={20}>
            <Flex gap={12}>
              {persistedState.user.empresa === null && (
                <Form.Item
                  label="Empresa"
                  name="idEmpresa"
                  rules={[
                    {
                      required: true,
                      message: "selecione uma empresa",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    optionFilterProp="labelSearch"
                    loading={empresaState.loading}
                    onChange={onChangeEmpresa}
                    style={{ width: 300 }}
                    options={empresaState.list.map((a: EmpresaModel) => ({
                      value: a.id,
                      key: a.apelido,
                      label: (
                        <Flex justify="flex-start" align="center" gap={8} style={{ padding: "1px 0px" }}>
                          <Flex style={{ lineHeight: "0px" }}>{a.urlLogotipoPequeno && <Image width={25} height={25} style={{ lineHeight: "0px" }} src={a.urlLogotipoPequeno} preview={false} />}</Flex>
                          {a.apelido}
                        </Flex>
                      ),
                      labelSearch: a.apelido,
                    }))}
                  />
                </Form.Item>
              )}
              <Form.Item
                label="Conta Bancária"
                name="idContaBanco"
                rules={[
                  {
                    required: true,
                    message: "selecione uma conta bancária",
                  },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  style={{ width: 200 }}
                  loading={contaBancoState.loading}
                  options={contaBancoState.list.map((a: Vm_ContaBancoModel) => ({
                    value: a.id,
                    label: a.denominacaoPersonalizada,
                    disabled: !a.ativo,
                  }))}
                />
              </Form.Item>
              <Form.Item label="Data início" name="dataInicio">
                <DatePicker placeholder="" format="DD/MM/YYYY" style={{ width: 120 }} />
              </Form.Item>
              <Form.Item label="Data fim" name="dataFim">
                <DatePicker placeholder="" format="DD/MM/YYYY" style={{ width: 120 }} />
              </Form.Item>
            </Flex>
          </Col>

          <Col span={4}>
            <Flex gap={12} justify="end">
              <Form.Item name="botaoCarregar" label=" ">
                <Button icon={<PrinterOutlined />} onClick={handlePrint} disabled={relatorioState.loadingMovimentacaoBancaria}></Button>
              </Form.Item>
              <Form.Item name="botaoCarregar" label=" ">
                <Button className="buttonPrimary" icon={<FilePdfOutlined />} htmlType="submit" disabled={relatorioState.loadingMovimentacaoBancaria}>
                  Carregar relatório
                </Button>
              </Form.Item>
            </Flex>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Flex gap={12}>
              <Form.Item label="Tipos de transação:" name="tipos">
                <Radio.Group optionType="button" buttonStyle="solid">
                  <Radio.Button value={"T"}>Todas</Radio.Button>
                  <Radio.Button value={"E"}>Entradas</Radio.Button>
                  <Radio.Button value={"S"}>Saídas</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Flex>
          </Col>
        </Row>
        <Row gutter={12} style={{ paddingTop: "24px" }}>
          <Col span={24}>
            {relatorioState.listMovimentacaoBancaria.length === 0 && !relatorioState.loadingMovimentacaoBancaria && (
              <div
                style={{
                  background: "white",
                  display: "grid",
                  width: "100%",
                  height: "500px",
                  alignItems: "center",
                }}
              >
                <Empty description={"O seu relatório será exibido aqui"} />
              </div>
            )}

            {relatorioState.listMovimentacaoBancaria.length === 0 && relatorioState.loadingMovimentacaoBancaria && <Skeleton.Button style={{ height: 500 }} active block />}
            {relatorioState.listMovimentacaoBancaria.length !== 0 && !relatorioState.loadingMovimentacaoBancaria && <RelatorioHtml ref={relatorioMovimentacao}></RelatorioHtml>}
          </Col>
        </Row>
      </Form>
    </Flex>
  );
};

export default MovimentacaoFinanceiraRelatorio;
